interface DropdownOptions {
  repositionFromBottom?: boolean;
  offset: { top: number; left: number };
  centerHorizontally?: boolean;
}

export default (
  options: DropdownOptions = { repositionFromBottom: true, offset: { top: 4, left: 0 }, centerHorizontally: false }
) => {
  const trigger = ref<HTMLElement | null>(null);
  const dropdown = ref<Modal | null>(null);

  const triggerWidth = ref("auto");
  const getTriggerWidth = () => {
    if (trigger.value?.offsetWidth) triggerWidth.value = trigger.value.offsetWidth + "px";
  };

  const showDropdown = ref(false);
  const toggleDropdown = () => {
    getTriggerWidth();
    showDropdown.value = !showDropdown.value;
  };

  const setStyleProperty = (property: string, value: string) => {
    dropdown.value?.setStyleProperty(property, value);
  };

  const calculatePosition = (cb?: (() => void) | null) => {
    if (!trigger.value || !dropdown.value) {
      console.error("Trigger or dropdown is not defined");
      return;
    }

    let triggerRect: DOMRect | null = null;
    triggerRect = trigger.value?.el?.getBoundingClientRect() || trigger.value?.getBoundingClientRect();

    if (triggerRect) {
      const dropdownHeight = dropdown.value?.height || 0;
      const dropdownWidth = dropdown.value?.width || 0;

      // Calculate vertical position
      const spaceBelow = window.innerHeight - triggerRect.bottom;

      if (spaceBelow >= dropdownHeight + options.offset.top) {
        // Position below trigger
        setStyleProperty("top", `${triggerRect.bottom + options.offset.top}px`);
      } else {
        // Position above trigger - dropdown bottom aligns with trigger top
        setStyleProperty("bottom", `${window.innerHeight - triggerRect.top + options.offset.top}px`);
      }

      // Calculate horizontal position
      if (options.centerHorizontally) {
        // Center the dropdown relative to the trigger
        const triggerCenter = triggerRect.left + triggerRect.width / 2;
        const dropdownLeft = triggerCenter - dropdownWidth / 2;

        // Ensure dropdown doesn't go off-screen
        const minLeft = 10; // 10px margin from edge
        const maxLeft = window.innerWidth - dropdownWidth - 10;
        const finalLeft = Math.max(minLeft, Math.min(dropdownLeft, maxLeft));

        setStyleProperty("left", `${finalLeft}px`);
      } else {
        // Original positioning logic
        const spaceRight = window.innerWidth - triggerRect.left;

        if (spaceRight >= dropdownWidth + options.offset.left) {
          // Position aligned with trigger left edge (normal case)
          setStyleProperty("left", `${triggerRect.left + options.offset.left}px`);
        } else {
          // Position aligned with trigger right edge when no space to the right
          setStyleProperty("right", `${window.innerWidth - triggerRect.right + options.offset.left}px`);
        }
      }
    }

    if (cb && typeof cb === "function") cb();
  };

  return {
    trigger,
    dropdown,
    getTriggerWidth,
    triggerWidth,
    showDropdown,
    toggleDropdown,
    setStyleProperty,
    calculatePosition,
  };
};
